import { Component, DestroyRef, EventEmitter, Input, Output, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { IonicModule } from '@ionic/angular';
import { OverlayLockService } from '../../../services/overlay-lock.service';
import { PushService } from '../../../services/push.service';

@Component({
  selector: 'app-push-prompt-modal',
  standalone: true,
  imports: [CommonModule, IonicModule],
  template: `
    <div class="fixed inset-0 z-[10000] flex items-center justify-center p-3 bg-black/75 backdrop-blur-sm animate-fade-in overflow-y-auto" style="padding-top: max(0.75rem, env(safe-area-inset-top)); padding-bottom: max(0.75rem, env(safe-area-inset-bottom))">
      <div class="relative w-full max-w-md bg-[#0F172A] border border-[#1E2E5D] rounded-3xl p-5 shadow-2xl shadow-emerald-500/10 text-white overflow-x-hidden overflow-y-auto" style="max-height: calc(100dvh - 1.5rem); overscroll-behavior: contain">
        <!-- Glow top ambient -->
        <div class="absolute -top-16 left-1/2 -translate-x-1/2 w-48 h-48 bg-gradient-to-b from-emerald-500/20 to-transparent rounded-full blur-2xl pointer-events-none"></div>

        <!-- Close 'X' Button -->
        <button
          (click)="onDismiss()"
          [disabled]="isActivating"
          class="absolute top-4 right-4 text-slate-400 hover:text-white p-1.5 rounded-full hover:bg-slate-800 transition-colors"
          title="Cerrar"
        >
          <ion-icon name="close" class="text-xl"></ion-icon>
        </button>

        <!-- Icon Banner -->
        <div class="flex justify-center mb-4">
          <div class="relative w-16 h-16 rounded-2xl bg-gradient-to-tr from-emerald-500 to-[#00F0FF] p-[2px] shadow-lg shadow-emerald-500/30">
            <div class="w-full h-full bg-[#0F172A] rounded-2xl flex items-center justify-center text-3xl">
              <span *ngIf="state !== 'success'">🔔</span>
              <span *ngIf="state === 'success'">🎉</span>
            </div>
            <!-- Notification pulse ping -->
            <span *ngIf="state === 'idle'" class="absolute -top-1 -right-1 flex h-4 w-4">
              <span class="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
              <span class="relative inline-flex rounded-full h-4 w-4 bg-emerald-500 border-2 border-[#0F172A]"></span>
            </span>
          </div>
        </div>

        <!-- Title & Subtitle -->
        <div class="text-center mb-5">
          <h3 class="text-lg font-black tracking-tight text-white mb-1.5">
            <span *ngIf="state !== 'success'">¿Activar Recordatorios de Pausas?</span>
            <span *ngIf="state === 'success'" class="text-emerald-400">¡Notificaciones Activadas!</span>
          </h3>
          <p class="text-xs text-slate-300 leading-relaxed px-2">
            <span *ngIf="state !== 'success'">
              Mantén tu salud laboral al día activando las alertas en este nuevo dispositivo.
            </span>
            <span *ngIf="state === 'success'">
              Te avisaremos en tus horarios programados para cuidar tu postura y energía.
            </span>
          </p>
        </div>

        <!-- 3 Feature benefits (only if not success) -->
        <div *ngIf="state !== 'success'" class="space-y-2.5 mb-6">
          <div class="flex items-center gap-3 p-2.5 rounded-2xl bg-[#162244]/60 border border-[#1E2E5D]/60 text-left">
            <div class="w-8 h-8 rounded-xl bg-emerald-500/15 text-emerald-400 flex items-center justify-center text-base shrink-0">
              ⏰
            </div>
            <div class="min-w-0">
              <h5 class="text-xs font-bold text-white">Horarios Oficiales SST</h5>
              <p class="text-[11px] text-slate-400">Alertas a las 10:00 AM y 4:00 PM (GMT-5).</p>
            </div>
          </div>

          <div class="flex items-center gap-3 p-2.5 rounded-2xl bg-[#162244]/60 border border-[#1E2E5D]/60 text-left">
            <div class="w-8 h-8 rounded-xl bg-cyan-500/15 text-cyan-400 flex items-center justify-center text-base shrink-0">
              🎮
            </div>
            <div class="min-w-0">
              <h5 class="text-xs font-bold text-white">Pausas Lúdicas 1 vs 1</h5>
              <p class="text-[11px] text-slate-400">Avisos instantáneos de retos con compañeros.</p>
            </div>
          </div>

          <div class="flex items-center gap-3 p-2.5 rounded-2xl bg-[#162244]/60 border border-[#1E2E5D]/60 text-left">
            <div class="w-8 h-8 rounded-xl bg-lime-500/15 text-lime-400 flex items-center justify-center text-base shrink-0">
              🛡️
            </div>
            <div class="min-w-0">
              <h5 class="text-xs font-bold text-white">100% Sin Spam</h5>
              <p class="text-[11px] text-slate-400">Solo avisos de bienestar, ergonomía y salud laboral.</p>
            </div>
          </div>
        </div>

        <!-- Error / Warning feedback -->
        <div *ngIf="feedbackMessage" class="mb-4 p-3 rounded-xl bg-amber-500/10 border border-amber-500/30 text-amber-300 text-xs text-center flex items-center justify-center gap-2">
          <span>⚠️</span>
          <span>{{ feedbackMessage }}</span>
        </div>

        <!-- Success feedback card -->
        <div *ngIf="state === 'success'" class="my-4 p-4 rounded-2xl bg-emerald-500/15 border border-emerald-500/40 text-emerald-300 text-xs text-center">
          <p class="font-bold text-sm mb-1">✓ Dispositivo vinculado con éxito</p>
          <p class="text-slate-300 text-[11px]">Puedes gestionar tus preferencias en cualquier momento desde tu Perfil.</p>
        </div>

        <!-- Action Buttons -->
        <div class="space-y-2">
          <button
            *ngIf="state !== 'success'"
            (click)="onActivate()"
            [disabled]="isActivating"
            class="w-full py-3 px-4 rounded-2xl text-xs font-black uppercase tracking-wider bg-gradient-to-r from-emerald-500 via-teal-500 to-[#00F0FF] text-slate-950 shadow-lg shadow-emerald-500/25 hover:brightness-110 active:scale-[0.99] transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <span *ngIf="isActivating" class="inline-block animate-spin">⏳</span>
            <span>{{ isActivating ? 'Solicitando permiso...' : '🔔 Activar en este dispositivo' }}</span>
          </button>

          <button
            *ngIf="state !== 'success'"
            (click)="onDismiss()"
            [disabled]="isActivating"
            class="w-full py-2.5 px-4 rounded-xl text-xs font-semibold text-slate-400 hover:text-white hover:bg-slate-800/60 transition-colors"
          >
            Ahora no / Recordar más tarde
          </button>
        </div>
      </div>
    </div>
  `
})
export class PushPromptModalComponent {
  private readonly overlayHold = inject(OverlayLockService).hold(inject(DestroyRef));

  @Input() userId!: string;
  @Output() closeModal = new EventEmitter<boolean>();

  state: 'idle' | 'requesting' | 'success' | 'error' = 'idle';
  isActivating: boolean = false;
  feedbackMessage: string | null = null;

  constructor(private readonly pushService: PushService) {}

  async onActivate(): Promise<void> {
    if (!this.userId || this.isActivating) return;

    this.isActivating = true;
    this.state = 'requesting';
    this.feedbackMessage = null;

    try {
      const granted = await this.pushService.enableNotifications(this.userId);
      if (granted) {
        this.state = 'success';
        if (typeof localStorage !== 'undefined') {
          localStorage.setItem('sb_push_prompt_handled_' + this.userId, 'granted');
        }
        setTimeout(() => {
          this.closeModal.emit(true);
        }, 1600);
      } else {
        this.state = 'idle';
        const perm =
          typeof Notification !== 'undefined' ? Notification.permission : 'unsupported';
        if (perm === 'denied') {
          this.feedbackMessage =
            'Las notificaciones están bloqueadas en tu navegador. Puedes habilitarlas desde la configuración de tu sitio.';
          if (typeof localStorage !== 'undefined') {
            localStorage.setItem('sb_push_prompt_handled_' + this.userId, 'denied');
          }
        } else if (perm === 'granted') {
          // El permiso SÍ quedó concedido aunque no se registrara el token:
          // no le decimos que falló y no volvemos a molestar con el aviso.
          this.feedbackMessage =
            'Permiso concedido, pero no pudimos registrar este dispositivo. Recarga la página; si persiste, actívalo desde tu Perfil.';
          if (typeof localStorage !== 'undefined') {
            localStorage.setItem('sb_push_prompt_handled_' + this.userId, 'granted');
          }
        } else {
          this.feedbackMessage =
            'No se completó la activación. Podrás activarlas cuando desees desde tu Perfil.';
          if (typeof localStorage !== 'undefined') {
            localStorage.setItem('sb_push_prompt_handled_' + this.userId, 'dismissed');
          }
        }
      }
    } catch (err: any) {
      console.error('[PushPromptModal] Error:', err);
      this.state = 'idle';
      this.feedbackMessage = 'Ocurrió un error al activar las notificaciones. Inténtalo de nuevo más tarde.';
    } finally {
      this.isActivating = false;
    }
  }

  onDismiss(): void {
    if (typeof localStorage !== 'undefined' && this.userId) {
      localStorage.setItem('sb_push_prompt_handled_' + this.userId, 'dismissed');
    }
    this.closeModal.emit(false);
  }
}
